import { Statement, Actor, Context, Verb, Result, Activity, ActivityDefinition, Extensions } from "@xapi/xapi";
import { API_URL_LRS_XAPI } from "utils/constants";
import { get, post } from "./httpService";
import {
  ItemAssessment,
  ItemConfigurationTypeAssessment,
  LRSProviderState,
  LRSRecord,
} from "@STRSERVICE/optima-component-library";
import { XapiKey } from "./lrsService";
import { ExtendedTreeNode, MapReferenceKey, MapReferenceKeys, ReferenceKey } from "types/cds";
import {
  getProgressedLearningObjectivesByQuestions,
  ItemKnowledge,
} from "./reportingService";
import { LRSProviderStateExtended, TestLabel } from "layout/test/test";
import { v4 as uuidv4 } from "uuid";
import { appConfig } from "appConfig";
import { RequestError } from "types/request";
import { UserEducation } from "types/education";

export const uriHost: string = "str-optima.se";

export interface IxApi {
  statementId: string;
  statementType: xApiStatementType;
  state: LRSProviderState;
  educationState: UserEducation
  key?: XapiKey;
  assessment?: ItemAssessment;
  assessmentLabel?: TestLabel;
  learningStructure?: ExtendedTreeNode[];
  questionState?: LRSRecord;
  assessmentState?: LRSProviderState;
}

const apiUrl = API_URL_LRS_XAPI;
type xApiStatementType = "question" | "assessment" | "assessment2";
type xApiActivityIdSegmentType = "question" | "assessment" | "activity" | "progressed";
type ExtendedActivityDefinition = ActivityDefinition & { interactionType?: string };

const getISO8601Time = (ms: number) => {
  const time = new Date(ms);
  const hours = time.getUTCHours();
  const minutes = time.getUTCMinutes();
  const seconds = time.getUTCSeconds();
  return `PT${hours}H${minutes}M${seconds}S`;
};

const createContext = (): Context => {
  return {
    platform: "STR-Optima",
    revision: "1.0.0",
  };
};

const createActor = (state: LRSProviderState): Actor => {
  return {
    objectType: "Agent",
    mbox: `mailto:${state.user.id}@${uriHost}`,
  };
};

const createVerb = (
  type:
    | "answered"
    | "attempted"
    | "completed"
    | "failed"
    | "passed"
    | "progressed"
): Verb => {
  const uri = !type.startsWith("reported-") ? "http://adlnet.gov/expapi/verbs/" : `http://${uriHost}/expapi/verbs/`;
  return {
    id: `${uri}${type}`,
    display: { "en-US": type },
  };
};

const createResult = (state: LRSProviderState): Result | undefined => {
  // *** TEMP KOD tills vi avgjort var vi hanterar konvertering ***
  if (!state.result) return undefined;
  let response = undefined;
  let duration = undefined;

  // Konvertera här tills vidare...
  if (state.result.response) {
    let responseArray = JSON.parse(state.result.response);
    const numberArray = responseArray.map((r: string) => Number(r));
    response = JSON.stringify(numberArray);
  }

  if (state.result.duration && state.result.duration.start && state.result.duration.end) {
    duration = getISO8601Time(state.result.duration.end - state.result.duration.start);
  }

  // *** TEMP KOD ***

  return {
    completion: state.result!.completion,
    response: response,
    success: state.result!.success,
    score: {
      min: state.result.score ? state.result.score?.min : undefined,
      max: state.result.score ? state.result.score?.max : undefined,
      raw: state.result.score ? state.result.score?.raw : undefined,
      scaled: 0,
    },
    duration: duration,
  };
};

const createObjectActivity = (referenceKey: ReferenceKey, segmentType: xApiActivityIdSegmentType): Activity => {
  return {
    objectType: "Activity",
    id: `http://${uriHost}/${segmentType}/${referenceKey.contentId}/${referenceKey.versionId}`,
  };
};

const createActivityDefinition = (name: string, interactionType?: string): ExtendedActivityDefinition => {
  return {
    interactionType: interactionType,
    name: {
      "en-US": name,
    },
  };
};

const createAndSaveXapiStatement = async ({
  statementId,
  statementType,
  state,
  educationState,
  key,
  assessment,
  assessmentLabel,
  questionState,
  learningStructure,
  assessmentState,
}: IxApi) => {
  let response: string[] = [];
  switch (statementType) {
    case "question":
      if (state.result?.response)
        // answered
        response = await saveXapiStatement(createXapiStatementA(statementId, state,educationState, assessmentState));
      break;
    case "assessment2":
      // Attempted
      response = await saveXapiStatement(createXapiStatementF(statementId, state,educationState));
      break;
    case "assessment":
      // None,
      // Study = 1,
      // Quick,
      // Custom,
      // Final,
      // Roadsigns,

      // AssessmentA & Study & Final
      // AssessmentB & Study
      // AssessmentB & Study | Quick | Custom
      // AssessmentB & Roadsigns

      // TODO: Add try catch
      if (!questionState) throw new Error("questionState must be defined");
      if (!learningStructure) throw new Error("learningStructure must be defined");
      if (!key) throw new Error("key must be defined");
      if (!assessmentLabel) throw new Error("assessmentLabel must be defined");
      if (!assessment) throw new Error("assessment must be defined");

      // Result from getProgressedLearningObjectivesByQuestions will include questions from assessmentRoadsigns as these are a subset of assessmentStudy
      const progressedLearningObjectivesByQuestions = getProgressedLearningObjectivesByQuestions(
        assessment,
        state,
        questionState,
        assessmentLabel
      );
      const progressedLearningObjectivesByAssessment = progressedLearningObjectivesByQuestions.questions.flatMap(
        (x) => x.learningObjectives
      );

      const rootActivityReferenceKey = getRootActivityReferenceKey(learningStructure);

      // completed - A, B, C, D
      response = await saveXapiStatement(
        createXapiStatementB(statementId, state,educationState, progressedLearningObjectivesByQuestions)
      );

      if (assessment.configuration.type !== ItemConfigurationTypeAssessment.AssessmentC) {
        // progressed - A, B, D
        await saveXapiStatement(
          createXapiStatementC(state, educationState, rootActivityReferenceKey, progressedLearningObjectivesByAssessment)
        );
      }

      break;
    default:
      throw new Error("No valid xapi statement type");
  }

  if (response.length > 0) return response[0];

  return undefined;
};

const getRootActivityReferenceKey = (learningStructure: ExtendedTreeNode[] | undefined) => {
  const key = learningStructure?.find((x) => x.parentId === 0)?.activityKey;
  if (!key) throw new Error("Connot find key for activity root node");
  return key;
};

const createGeneralExtensions = (extensions: Extensions, educationState: UserEducation) => {
  extensions[`http://${uriHost}/collection/id`] = educationState.collectionId;
  extensions[`http://${uriHost}/context/id`] = {
    contentId: educationState.contentId,
    versionId: educationState.versionId
  } as ReferenceKey;
};

const createXapiStatementA = (statementId: string, state: LRSProviderState, educationState: UserEducation, assessmentState?: LRSProviderState) => {
  const segmentType: xApiActivityIdSegmentType = "question";

  const activity = createObjectActivity(state.referenceKey, segmentType);
  activity.definition = createActivityDefinition("Question", "choice"); // TODO: Check interactionType

  activity.definition.extensions = {
    [`http://${uriHost}/${segmentType}/referenceKey`]: state.referenceKey,
    [`http://${uriHost}/${segmentType}`]: { correctResponse: state.correctResponse, optionOrder: state.optionOrder },
  };

  createGeneralExtensions(activity.definition.extensions, educationState);

  if (assessmentState) {
    activity.definition.extensions[`http://${uriHost}/assessment/referenceKey`] = assessmentState.referenceKey;
  }

  const statement: Statement = {
    id: statementId,
    object: activity,
    context: createContext(),
    actor: createActor(state),
    verb: createVerb("answered"),
    result: createResult(state),
    timestamp: new Date().toISOString(),
  };

  return statement;
};

const createXapiStatementB = (statementId: string, state: LRSProviderStateExtended, educationState: UserEducation, progress: any) => {
  const segmentType: xApiActivityIdSegmentType = "assessment";

  const activity = createObjectActivity(state.referenceKey, segmentType);
  activity.definition = createActivityDefinition("Assessment");

  activity.definition.extensions = {
    [`http://${uriHost}/${segmentType}/referenceKey`]: state.referenceKey,
    [`http://${uriHost}/${segmentType}/attemptId`]: state.attemptId,
    [`http://${uriHost}/${segmentType}`]: progress,
  };

  createGeneralExtensions(activity.definition.extensions, educationState,);

  const statement: Statement = {
    id: statementId,
    object: activity,
    context: createContext(),
    actor: createActor(state),
    verb: createVerb("completed"),
    result: createResult(state),
    timestamp: new Date().toISOString(),
  };

  return statement;
};

const createXapiStatementC = (
  state: LRSProviderState,
  educationState: UserEducation,
  rootActivityReferenceKey: ReferenceKey,
  progressedLearningObjectives: string[]
) => {
  const segmentType: xApiActivityIdSegmentType = "activity";

  const activity = createObjectActivity(rootActivityReferenceKey, segmentType);
  activity.definition = createActivityDefinition("Activity");

  activity.definition.extensions = {
    [`http://${uriHost}/${segmentType}/referenceKey`]: rootActivityReferenceKey,
    [`http://${uriHost}/learningObjective/id`]: progressedLearningObjectives,
  };

  createGeneralExtensions(activity.definition.extensions, educationState);

  const statement: Statement = {
    object: activity,
    context: createContext(),
    actor: createActor(state),
    verb: createVerb("progressed"),
    timestamp: new Date().toISOString(),
  };

  return statement;
};

const createXapiStatementF = (
  statementId: string, 
  state: LRSProviderState,
  educationState: UserEducation,
) => {
  const segmentType: xApiActivityIdSegmentType = "assessment";

  const activity = createObjectActivity(state.referenceKey, segmentType);
  activity.definition = createActivityDefinition("Assessment");

  activity.definition.extensions = {
    [`http://${uriHost}/${segmentType}/referenceKey`]: state.referenceKey,
    [`http://${uriHost}/${segmentType}`]: state.questions,
  };

  createGeneralExtensions(activity.definition.extensions, educationState);

  const statement: Statement = {
    id: statementId,
    object: activity,
    context: createContext(),
    actor: createActor(state),
    verb: createVerb("attempted"),
    result: createResult(state),
    timestamp: new Date().toISOString(),
  };

  return statement;
};

const getProgressReport = async (master: ItemAssessment, userId: string, referenceKey: ReferenceKey) => {
  const response: ItemKnowledge = await get(
    `${appConfig.cdsApiUrl}/v1/actors/progress/kkb/${referenceKey.contentId}/${referenceKey.versionId}/${userId}`
  );

  return response;
};

const getTestReport = async (userId: string, referenceKey: ReferenceKey) => {
  const response: ItemKnowledge = await get(
    `${appConfig.cdsApiUrl}/v1/actors/result/kkb/${referenceKey.contentId}/${referenceKey.versionId}/${userId}`
  );

  return response;
};

const getStudyHelp = async (userId: string, referenceKey: ReferenceKey, id: string) => {
  const response: MapReferenceKeys = await get(
    `${appConfig.cdsApiUrl}/v1/actors/feedback/kkb/${referenceKey.contentId}/${referenceKey.versionId}/${userId}/${id}`
  );

  return response;
};

const getStudyHelpFinal = async (userId: string, referenceKey: ReferenceKey, id: string) => {
  const response: MapReferenceKey | RequestError = await get(
    `${appConfig.cdsApiUrl}/v1/actors/feedback-final/kkb/${referenceKey.contentId}/${referenceKey.versionId}/${userId}/${id}`
  );

  return response;
};

const saveXapiStatement = async (statement: Statement): Promise<string[]> => {
  const url = apiUrl + "statements";
  let saved = false;
  let response: string[] = [];
  if (statement.id === undefined) {
    statement.id = uuidv4();
  }
  while (saved === false) {
    const response = await post(url, [statement]);
    saved = response.length > 0;
    if (!saved) {
      statement.id = uuidv4();
    }
  }

  return response;
};

const getXapiStatement = async (id: string) => {
  const url = apiUrl + `statements/${id}`;
  const response: Statement = await get(url);
  // TODO: Fix the incorrect response from the endpoint instead
  try {
    return response;
  } catch (e) {
    return null;
  }
};

export {
  createAndSaveXapiStatement,
  getXapiStatement,
  getProgressReport,
  getTestReport,
  getStudyHelp,
  getStudyHelpFinal,
};
